/**
 * Maps a backend `user_modules[i].name` to the local UI route, PrimeIcon, and
 * the dot-delimited group path it should appear under in the top header
 * menubar.
 *
 * **This registry is the authoritative allowlist** — it mirrors the legacy
 * `futureNav` arrays in `fb-shipper-frontend/src/app/services/index.util.service.js`
 * (`shipperList` / `forwarderList` / `freightbroList`). Modules absent from
 * this map are silently dropped from the menu, matching legacy behavior.
 *
 * Mental model:
 *   - `parent: ''`                                → top-level entry
 *   - `parent: 'My Company'`                      → "My Company" submenu
 *   - `parent: 'Manage Rates.Contract Rates.Maintain Rates'`
 *                                                  → 3-level deep nested submenu
 *   - `weight` (lower = earlier) controls sibling ordering inside a group.
 *   - `label` (optional) overrides the API-provided `title` — only used as a
 *     defensive fallback when `title` is empty.
 *
 * Top-level slot weights are intentionally **sparse** so the rendered order is
 * stable regardless of which children a tenant happens to carry:
 *
 *      Analytics (group, inherits 1)        → slot 1
 *      Rate Search (search)                 → slot 10
 *      Enquiries (inquiry / enquiries)      → slot 20
 *      Quotes (quotes)                      → slot 30
 *      Bookings (shipments / bookings)      → slot 40
 *      LCL Pricing Matrix (lclMatrixView)   → slot 50
 *      My Company (group, min child = 60)   → slot 60
 *      Manage Rates (group, min child = 80) → slot 80
 *
 * Inner siblings keep their relative order via small offsets (e.g. 60, 61, 62
 * inside My Company; 80, 81, 82 inside Manage Rates).
 *
 * To onboard a new module: add the entry here, register the matching route in
 * `apps/platform/src/app/app.routes.ts`, and (if it should sit under a new
 * group) just spell the new group name in `parent` — groups are created on
 * demand by the mapper.
 */

/**
 * A leaf the mapper synthesises in place of a real `user_modules` entry.
 *
 * Mirrors the legacy `fnLoopNavigation` special-case where a single
 * permissioned module fans out into multiple visible menu rows — e.g.
 * `configuration` becomes "Company Information" + "Booking Status" inside
 * `My Company › Settings` (see `fb-shipper-frontend/src/app/services/index.util.service.js`
 * lines 1429-1451). The virtual children inherit the source module's
 * permission set and `is_paid` flag.
 */
export interface VirtualChild {
  readonly id: string;
  readonly label: string;
  readonly route: string;
  readonly icon: string;
  readonly weight: number;
}

export interface ModuleRouteMeta {
  readonly route: string;
  readonly icon: string;
  readonly parent?: string;
  readonly weight?: number;
  readonly label?: string;
  /**
   * If set, the mapper appends each virtual leaf under the same `parent` path
   * **instead of** rendering this module as its own leaf. Use this whenever a
   * single backend module needs to surface as several menu rows.
   */
  readonly virtualChildren?: readonly VirtualChild[];
}

export const MODULE_REGISTRY: Readonly<Record<string, ModuleRouteMeta>> = {
  // ── Analytics group (slot 1) ────────────────────────────────────────────
  dashboard: { route: '/dashboard', icon: 'pi pi-home', parent: 'Analytics', weight: 1 },
  reports: { route: '/reports', icon: 'pi pi-chart-bar', parent: 'Analytics', weight: 2 },

  // ── Top-level leaves (slots 10 → 50) ────────────────────────────────────
  search: { route: '/search', icon: 'pi pi-search', parent: '', weight: 10, label: 'Rate Search' },
  inquiry: { route: '/enquiries', icon: 'pi pi-inbox', parent: '', weight: 20, label: 'Enquiries' },
  enquiries: { route: '/enquiries', icon: 'pi pi-inbox', parent: '', weight: 20, label: 'Enquiries' },
  quotes: { route: '/quotes', icon: 'pi pi-file', parent: '', weight: 30, label: 'Quotes' },
  shipments: { route: '/shipments', icon: 'pi pi-box', parent: '', weight: 40, label: 'Bookings' },
  bookings: { route: '/bookings', icon: 'pi pi-calendar', parent: '', weight: 40, label: 'Bookings' },
  lclMatrixView: { route: '/lcl-matrix', icon: 'pi pi-table', parent: '', weight: 50, label: 'LCL Pricing Matrix' },

  // ── My Company group (slot 60) ─────────────────────────────────────────
  manageNotification: { route: '/notifications/manage', icon: 'pi pi-bell', parent: 'My Company', weight: 60 },
  bannerBroadcast: { route: '/notifications/broadcast', icon: 'pi pi-megaphone', parent: 'My Company', weight: 61 },
  forwarders: { route: '/company/forwarders', icon: 'pi pi-building', parent: 'My Company', weight: 62 },
  groups: { route: '/company/groups', icon: 'pi pi-th-large', parent: 'My Company', weight: 63 },
  shippers: { route: '/company/shippers', icon: 'pi pi-users', parent: 'My Company', weight: 64 },
  agents: { route: '/company/agents', icon: 'pi pi-id-card', parent: 'My Company', weight: 65 },
  users: { route: '/company/users', icon: 'pi pi-user', parent: 'My Company', weight: 66 },
  carrierIntegration: { route: '/integrations/carriers', icon: 'pi pi-link', parent: 'My Company', weight: 67 },

  // ── My Company › Settings sub-group (slot range 70-79) ─────────────────
  // `configuration` is a *synthetic parent* — instead of emitting its own
  // leaf, we fan it out into "Company Information" + "Booking Status" inside
  // Settings (legacy parity: see `fb-shipper-frontend/src/app/services/index.util.service.js`
  // lines 1429-1451). Virtual-child weights stay in the Settings range so
  // they never bubble up and disturb the locked top-level menubar order.
  configuration: {
    route: '',
    icon: '',
    parent: 'My Company.Settings',
    weight: 70,
    virtualChildren: [
      {
        id: 'companyInformation',
        label: 'Company Information',
        route: '/settings/company-information',
        icon: 'pi pi-building',
        weight: 70,
      },
      {
        id: 'shipmentStatus',
        label: 'Booking Status',
        route: '/settings/shipment-status',
        icon: 'pi pi-check',
        weight: 75,
      },
    ],
  },
  roles: { route: '/settings/roles', icon: 'pi pi-shield', parent: 'My Company.Settings', weight: 71 },
  teams: { route: '/settings/teams', icon: 'pi pi-sitemap', parent: 'My Company.Settings', weight: 72 },
  companyGroups: {
    route: '/settings/company-groups',
    icon: 'pi pi-th-large',
    parent: 'My Company.Settings',
    weight: 73,
  },
  tags: { route: '/settings/tags', icon: 'pi pi-tag', parent: 'My Company.Settings', weight: 74 },
  assignShippers: {
    route: '/settings/assign-shippers',
    icon: 'pi pi-users',
    parent: 'My Company.Settings',
    weight: 76,
  },

  // ── Manage Rates group (slot 80) ───────────────────────────────────────
  rules: { route: '/rates/rules', icon: 'pi pi-sliders-h', parent: 'Manage Rates', weight: 80 },
  manageRates: { route: '/rates/manage', icon: 'pi pi-cog', parent: 'Manage Rates', weight: 81 },
  currencySetting: { route: '/rates/currency', icon: 'pi pi-dollar', parent: 'Manage Rates', weight: 82 },
  rates: { route: '/rates', icon: 'pi pi-tags', parent: 'Manage Rates', weight: 83 },
  charges: { route: '/rates/charges', icon: 'pi pi-receipt', parent: 'Manage Rates', weight: 84 },

  // ── Manage Rates › Contract Rates sub-group ────────────────────────────
  myRatesV2: {
    route: '/contracts/rate-files',
    icon: 'pi pi-file-edit',
    parent: 'Manage Rates.Contract Rates',
    weight: 90,
  },
  tariffHeader: { route: '/contracts/tariffs', icon: 'pi pi-list', parent: 'Manage Rates.Contract Rates', weight: 91 },
  myRates: { route: '/contracts/my-rates', icon: 'pi pi-tags', parent: 'Manage Rates.Contract Rates', weight: 92 },
  modifyRates: { route: '/contracts/modify', icon: 'pi pi-pencil', parent: 'Manage Rates.Contract Rates', weight: 93 },

  // ── Manage Rates › Contract Rates › Contract Manager sub-group ─────────
  contractManager: {
    route: '/contracts/manager',
    icon: 'pi pi-bookmark',
    parent: 'Manage Rates.Contract Rates.Contract Manager',
    weight: 100,
  },
  cdmAuditTrail: {
    route: '/contracts/audit-trail',
    icon: 'pi pi-history',
    parent: 'Manage Rates.Contract Rates.Contract Manager',
    weight: 101,
  },
  cdmDownloadContracts: {
    route: '/contracts/downloads',
    icon: 'pi pi-download',
    parent: 'Manage Rates.Contract Rates.Contract Manager',
    weight: 102,
  },

  // ── Manage Rates › Contract Rates › Maintain Rates sub-group ──────────
  dauFclLeg: {
    route: '/maintain/dau-fcl',
    icon: 'pi pi-link',
    parent: 'Manage Rates.Contract Rates.Maintain Rates',
    weight: 110,
  },
  bulkUpdate: {
    route: '/maintain/bulk-update',
    icon: 'pi pi-replay',
    parent: 'Manage Rates.Contract Rates.Maintain Rates',
    weight: 111,
  },
  auditTrail: {
    route: '/maintain/audit-trail',
    icon: 'pi pi-history',
    parent: 'Manage Rates.Contract Rates.Maintain Rates',
    weight: 112,
  },

  // NOTE — modules deliberately *omitted* from the registry (and thus from
  // the menu, per the strict allowlist):
  //   • createShipment / createQuote / addRate / uploadRateSheet — exposed
  //     via the right-side "+ New" button, never the menubar (legacy parity).
  //   • trackNTrace, uploadRatesV2 — not in the legacy shipper menu screenshot.
  //   • profile — explicitly excluded by `userModulesToNavItems` (handled in
  //     the avatar dropdown, like legacy `value.name !== "profile"`).
};
